// Payroll Settings → "Overtime & Hours": the per-legal-entity, config-driven rule
// for pricing worked hours (overtime / public-holiday / night) into pay — and a
// file importer to feed a period's hours in. HR/Finance tune the divisor and each
// bucket's factor/flat-rate; a live preview prices a sample as they edit. The
// importer maps a spreadsheet's columns to buckets, matches employees by name, and
// stages the hours (priced at run time by this policy).
import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Save, Plus, Trash2, Upload, Clock, CheckCircle2, Link2, ScanLine } from 'lucide-react';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { listLegalEntities, type LegalEntity } from '@/services/payrollSettingsService';
import { listPayrollEmployees, type PayrollEmployeeLite } from '@/services/terminalDuesService';
import { extractTimesheet } from '@/lib/hrApi';
import { getApiErrorMessage } from '@/lib/apiError';
import { Button } from '@/features/shared/components/ui/button';
import { useEmployeeNames } from '@/features/pages/workassignments/shared';
import { type ProposedHour, decideProposedHours, listProposedHours } from '@/services/hoursPolicyService';
import {
  effectiveHoursSources, getHoursPolicy, setHoursPolicy, importHours, previewAmount, fetchHoursSource,
  type HoursPolicy, type HourType, type ImportHoursRow,
} from '@/services/hoursPolicyService';

const fieldCls =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring';
const labelCls = 'text-xs font-medium text-muted-foreground';
const num = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: 2 });

export const OvertimeHoursTab: React.FC = () => {
  const [entities, setEntities] = useState<LegalEntity[]>([]);
  const [entityId, setEntityId] = useState('');

  useEffect(() => {
    listLegalEntities().then((l) => { setEntities(l); if (l[0]) setEntityId((c) => c || l[0].id); }).catch(() => {});
  }, []);

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-5 space-y-2">
          <h2 className="text-lg font-medium text-foreground flex items-center gap-2">
            <Clock className="h-5 w-5 text-primary-500" /> Overtime &amp; Hours
          </h2>
          <p className="text-sm text-muted-foreground">
            How worked hours become pay — configurable per legal entity, so each company’s calculation is data, not
            code. Set the hourly basis and each hour type’s rate; imported hours are priced by this policy on the run.
          </p>
          <label className="space-y-1.5 block max-w-md pt-1">
            <span className={labelCls}>Legal entity</span>
            <select className={fieldCls} value={entityId} onChange={(e) => setEntityId(e.target.value)}>
              {entities.map((en) => <option key={en.id} value={en.id}>{en.name} ({en.country})</option>)}
            </select>
          </label>
        </CardContent>
      </Card>

      {entityId && <PolicyEditor key={entityId} entityId={entityId} />}
      <ProposedWorkHours />
      {entityId && <HoursImport key={'imp-' + entityId} entityId={entityId} />}
    </div>
  );
};

/** Work hours-source review queue: completed work items stage hours as
 *  PROPOSED — structurally invisible to the run calc until approved here.
 *  Reject → cancelled. Pricing additionally requires the entity's policy to
 *  opt into the 'work' source above. */
const ProposedWorkHours: React.FC = () => {
  const [rows, setRows] = useState<ProposedHour[]>([]);
  const [sel, setSel] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState(false);
  const { nameOf } = useEmployeeNames('all');
  const load = () => {
    listProposedHours().then(setRows)
      .catch((e) => toast.error(getApiErrorMessage(e, 'Could not load proposed work hours.')));
  };
  useEffect(load, []);
  const ids = Object.keys(sel).filter((k) => sel[k]);
  const decide = async (approve: boolean) => {
    setBusy(true);
    try {
      const n = await decideProposedHours(ids, approve);
      toast.success(approve ? `${n} hour row(s) approved — payable on the next covering run.` : `${n} row(s) rejected.`);
      setSel({});
      load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not update the proposed hours.'));
    } finally {
      setBusy(false);
    }
  };
  if (rows.length === 0) return null;
  return (
    <Card>
      <CardContent className="p-5 space-y-3">
        <h3 className="text-base font-medium text-foreground">Proposed work hours · {rows.length}</h3>
        <p className="text-sm text-muted-foreground">
          Effort recorded on approved work items. Nothing here is payable until you approve it —
          and pricing still requires the “Completed work items” source above.
        </p>
        <div className="space-y-1">
          {rows.map((r) => (
            <label key={r.id} className="flex items-center gap-3 text-sm text-foreground border-b border-border py-2">
              <input type="checkbox" checked={!!sel[r.id]} onChange={(e) => setSel((s) => ({ ...s, [r.id]: e.target.checked }))} />
              <span className="min-w-0 flex-1 truncate">{nameOf(r.employeeId)}</span>
              <span className="tabular-nums">{r.hours ?? '—'} h</span>
              <span className="text-xs text-muted-foreground">{r.hourType ?? 'REGULAR'}</span>
              <span className="text-xs text-muted-foreground tabular-nums">{new Date(r.periodMonth).toLocaleDateString([], { month: 'short', year: 'numeric' })}</span>
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" disabled={busy || ids.length === 0} onClick={() => decide(false)}>Reject</Button>
          <Button size="sm" disabled={busy || ids.length === 0} onClick={() => decide(true)}>Approve for pay</Button>
        </div>
      </CardContent>
    </Card>
  );
};

// ------------------------------------------------------------------ policy
const BLANK_TYPE: HourType = { code: '', label: '', mode: 'factor', factor: 1.5, taxable: true };

const PolicyEditor: React.FC<{ entityId: string }> = ({ entityId }) => {
  const [policy, setPolicy] = useState<HoursPolicy | null>(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Kept as STRINGS: a numeric state + Number(e.target.value) snaps an empty
  // field back to 0, which made these impossible to type into (only the
  // spinner arrows worked). Parsed where used.
  const [sampleBasic, setSampleBasic] = useState('450000');
  const [sampleHours, setSampleHours] = useState('10');

  useEffect(() => {
    getHoursPolicy(entityId).then(setPolicy).catch(() => setPolicy(null));
  }, [entityId]);

  if (!policy) {
    return <Card><CardContent className="p-5 text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading policy…</CardContent></Card>;
  }

  const setBasis = (patch: Partial<HoursPolicy['hourly_basis']>) =>
    setPolicy({ ...policy, hourly_basis: { ...policy.hourly_basis, ...patch } });
  const setType = (i: number, patch: Partial<HourType>) =>
    setPolicy({ ...policy, hour_types: policy.hour_types.map((t, j) => (j === i ? { ...t, ...patch } : t)) });
  const addType = () => setPolicy({ ...policy, hour_types: [...policy.hour_types, { ...BLANK_TYPE }] });
  const removeType = (i: number) => setPolicy({ ...policy, hour_types: policy.hour_types.filter((_, j) => j !== i) });

  const save = async () => {
    setError(null); setBusy(true);
    try {
      const saved = await setHoursPolicy(entityId, policy);
      setPolicy(saved);
      setToast('Policy saved'); setTimeout(() => setToast(null), 2500);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Could not save the policy.');
    } finally { setBusy(false); }
  };

  return (
    <Card>
      <CardContent className="p-5 space-y-5">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">Pricing policy</h3>
          <span className="text-xs text-muted-foreground">{policy.version > 0 ? `v${policy.version}` : 'statutory default (unsaved)'}</span>
        </div>

        {/* Hourly basis */}
        <div className="grid gap-4 sm:grid-cols-3">
          <label className="space-y-1.5"><span className={labelCls}>Hourly rate basis</span>
            <select className={fieldCls} value={policy.hourly_basis.mode} onChange={(e) => setBasis({ mode: e.target.value as any })}>
              <option value="divisor">Basic ÷ divisor</option>
              <option value="explicit">Explicit hourly rate</option>
            </select></label>
          {policy.hourly_basis.mode === 'divisor' ? (
            <label className="space-y-1.5"><span className={labelCls}>Divisor (hours/month)</span>
              <input type="number" className={fieldCls} value={policy.hourly_basis.divisor ?? ''} onChange={(e) => setBasis({ divisor: Number(e.target.value) })} />
              <span className="text-[11px] text-muted-foreground">195 = statutory (45h/wk). Eagle uses 240 (30×8).</span></label>
          ) : (
            <label className="space-y-1.5"><span className={labelCls}>Hourly rate</span>
              <input type="number" className={fieldCls} value={policy.hourly_basis.rate ?? ''} onChange={(e) => setBasis({ rate: Number(e.target.value) })} /></label>
          )}
          <div className="space-y-1.5"><span className={labelCls}>Hours come from</span>
            <div className="space-y-1">
              {([
                ['file', 'File import'],
                ['attendance', 'In-system attendance'],
                ['assignment', 'Work assignments (field/per-diem days)'],
                ['work', 'Completed work items (Work Management — reviewed below)'],
              ] as const).map(([src, label]) => {
                const set = effectiveHoursSources(policy);
                const checked = set.includes(src);
                return (
                  <label key={src} className="flex items-center gap-2 text-sm text-foreground">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        const next = checked ? set.filter((x) => x !== src) : [...set, src];
                        setPolicy({ ...policy, hours_sources: next, hours_source: '' });
                      }}
                    />
                    {label}
                  </label>
                );
              })}
            </div>
            <span className="text-[11px] text-muted-foreground">
              {effectiveHoursSources(policy).length === 0
                ? 'Auto — whatever is provided gets priced. Tick sources to price only those.'
                : 'Only ticked sources are priced — a stray import can’t double-pay ticked-off sources.'}
            </span></div>
        </div>

        {/* Hour types */}
        <div className="space-y-2">
          <span className={labelCls}>Hour types</span>
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground bg-muted/50">
                  <th className="p-2 font-medium">Code</th>
                  <th className="p-2 font-medium">Label</th>
                  <th className="p-2 font-medium">How</th>
                  <th className="p-2 font-medium">Value</th>
                  <th className="p-2 font-medium">Taxable</th>
                  <th className="p-2" />
                </tr>
              </thead>
              <tbody>
                {policy.hour_types.map((t, i) => (
                  <tr key={i} className="border-t border-border">
                    <td className="p-1.5"><input className={fieldCls + ' py-1'} value={t.code} placeholder="OVERTIME" onChange={(e) => setType(i, { code: e.target.value.toUpperCase() })} /></td>
                    <td className="p-1.5"><input className={fieldCls + ' py-1'} value={t.label} placeholder="Overtime" onChange={(e) => setType(i, { label: e.target.value })} /></td>
                    <td className="p-1.5">
                      <select className={fieldCls + ' py-1'} value={t.mode} onChange={(e) => setType(i, { mode: e.target.value as any })}>
                        <option value="factor">× hourly</option>
                        <option value="flat_per_hour">flat / hr</option>
                      </select>
                    </td>
                    <td className="p-1.5">
                      {t.mode === 'factor' ? (
                        <input type="number" step="0.1" className={fieldCls + ' py-1'} value={t.factor ?? ''} onChange={(e) => setType(i, { factor: Number(e.target.value) })} />
                      ) : (
                        <input type="number" className={fieldCls + ' py-1'} value={t.rate ?? ''} onChange={(e) => setType(i, { rate: Number(e.target.value) })} />
                      )}
                    </td>
                    <td className="p-1.5 text-center"><input type="checkbox" checked={t.taxable} onChange={(e) => setType(i, { taxable: e.target.checked })} /></td>
                    <td className="p-1.5 text-right">
                      <button type="button" onClick={() => removeType(i)} aria-label="Remove" className="p-1 rounded text-muted-foreground hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Button variant="outline" size="sm" onClick={addType}><Plus className="h-4 w-4 mr-1" /> Add hour type</Button>
        </div>

        {/* Live preview */}
        <div className="rounded-md bg-muted/40 p-3 space-y-2">
          <div className="flex flex-wrap items-end gap-3">
            <label className="space-y-1"><span className={labelCls}>Sample basic salary</span>
              <input type="number" inputMode="decimal" className={fieldCls + ' py-1 max-w-[160px]'} value={sampleBasic} onChange={(e) => setSampleBasic(e.target.value)} /></label>
            <label className="space-y-1"><span className={labelCls}>Hours</span>
              <input type="number" inputMode="decimal" className={fieldCls + ' py-1 max-w-[100px]'} value={sampleHours} onChange={(e) => setSampleHours(e.target.value)} /></label>
          </div>
          <div className="flex flex-wrap gap-2 text-sm">
            {policy.hour_types.filter((t) => t.code).map((t) => {
              const amt = previewAmount(policy, t.code, Number(sampleHours) || 0, Number(sampleBasic) || 0);
              return (
                <span key={t.code} className="rounded-full bg-background border border-border px-2.5 py-1">
                  {t.label || t.code}: <strong className="tabular-nums">{amt == null ? '—' : num(amt)}</strong>
                </span>
              );
            })}
          </div>
          <p className="text-[11px] text-muted-foreground">Preview mirrors the run engine. Overtime is always taxable income into PAYE/NSSF/SDL/WCF — you set the rate, not whether it’s taxed.</p>
        </div>

        {error && <p className="text-sm text-danger">{error}</p>}
        <div className="flex items-center gap-3">
          <Button onClick={save} disabled={busy}>
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Save policy
          </Button>
          {toast && <span className="flex items-center gap-2 text-sm text-success"><CheckCircle2 className="h-4 w-4" /> {toast}</span>}
        </div>
      </CardContent>
    </Card>
  );
};

// ------------------------------------------------------------------ import
type ParsedCsv = { headers: string[]; rows: string[][] };

function parseCsv(text: string): ParsedCsv {
  const clean = text.replace(/^﻿/, '').trim();
  const lines = clean.split(/\r?\n/).filter((l) => l.trim() !== '');
  const split = (l: string) => l.split(',').map((c) => c.trim());
  const headers = lines.length ? split(lines[0]) : [];
  const rows = lines.slice(1).map(split);
  return { headers, rows };
}

// ---- name matching (Migration-Center-style: suggest, then let a human resolve) ----
// Normalizes away case, punctuation and double spaces, then matches on name
// TOKENS so "Glory Kassale" finds "Glory Edward Kassale" (middle names, order,
// extra spaces). Anything not confidently matched is left for the user to
// assign from a dropdown — never silently dropped.
const normName = (s: string) => s.toLowerCase().replace(/[^a-z\u00C0-\u024F ]+/g, ' ').replace(/\s+/g, ' ').trim();
const nameTokens = (s: string) => normName(s).split(' ').filter(Boolean);

type MatchSuggestion = { emp: PayrollEmployeeLite | null; level: 'exact' | 'strong' | 'none' };

function suggestMatch(fileName: string, employees: PayrollEmployeeLite[]): MatchSuggestion {
  const n = normName(fileName);
  if (!n) return { emp: null, level: 'none' };
  const exact = employees.find((e) => normName(e.full_name) === n);
  if (exact) return { emp: exact, level: 'exact' };
  const ft = nameTokens(fileName);
  const candidates = employees.filter((e) => {
    const et = nameTokens(e.full_name);
    const subset = (a: string[], b: string[]) => a.every((t) => b.includes(t));
    const overlap = ft.filter((t) => et.includes(t)).length;
    return overlap >= 2 && (subset(ft, et) || subset(et, ft));
  });
  if (candidates.length === 1) return { emp: candidates[0], level: 'strong' };
  return { emp: null, level: 'none' };
}

const HoursImport: React.FC<{ entityId: string }> = ({ entityId }) => {
  const [policy, setPolicy] = useState<HoursPolicy | null>(null);
  const [employees, setEmployees] = useState<PayrollEmployeeLite[]>([]);
  const [period, setPeriod] = useState(() => new Date().toISOString().slice(0, 7));
  const [csv, setCsv] = useState<ParsedCsv | null>(null);
  const [nameCol, setNameCol] = useState(0);
  const [colMap, setColMap] = useState<Record<number, string>>({}); // csv column index → hour-type code ('' = ignore)
  const [result, setResult] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  // Input source: upload a file, pull from a connected sheet, or scan a photo/PDF.
  const [mode, setMode] = useState<'file' | 'pull' | 'scan'>('file');
  const [sourceUrl, setSourceUrl] = useState('');
  const [loadingSrc, setLoadingSrc] = useState(false);
  const [srcErr, setSrcErr] = useState<string | null>(null);

  useEffect(() => {
    getHoursPolicy(entityId).then(setPolicy).catch(() => setPolicy(null));
    listPayrollEmployees().then(setEmployees).catch(() => setEmployees([]));
  }, [entityId]);

  // Per-file-row resolution: row index -> employee_id ('' = skip this row).
  // Seeded from the auto-matcher whenever a new file/employee list arrives;
  // the user can then re-assign or skip any row in the review table.
  const [assign, setAssign] = useState<Record<number, string>>({});
  const suggestions = useMemo(() => {
    if (!csv) return {} as Record<number, MatchSuggestion>;
    const out: Record<number, MatchSuggestion> = {};
    csv.rows.forEach((r, i) => {
      const name = (r[nameCol] || '').trim();
      if (!name || name.toLowerCase() === 'total') return;
      out[i] = suggestMatch(name, employees);
    });
    return out;
  }, [csv, employees, nameCol]);
  useEffect(() => {
    const next: Record<number, string> = {};
    Object.entries(suggestions).forEach(([i, sug]) => {
      next[Number(i)] = sug.emp ? sug.emp.employee_id : '';
    });
    setAssign(next);
  }, [suggestions]);
  const empById = useMemo(() => {
    const m: Record<string, PayrollEmployeeLite> = {};
    employees.forEach((e) => (m[e.employee_id] = e));
    return m;
  }, [employees]);
  const sortedEmployees = useMemo(
    () => [...employees].sort((a, b) => a.full_name.localeCompare(b.full_name)),
    [employees],
  );

  // Any source (file / connected pull / OCR scan) produces {headers, rows}; from
  // there the map → match → preview → import flow is identical.
  const applyParsed = (parsed: ParsedCsv) => {
    setCsv(parsed);
    setResult(null);
    setSrcErr(null);
    setNameCol(0);
    const guess: Record<number, string> = {};
    parsed.headers.forEach((h, i) => {
      if (i === 0) return;
      const l = h.toLowerCase();
      // Derived/summary columns must never auto-map — a "Total PH-OT" column
      // matching 'ph' silently REPLACED the real public-holiday column and
      // double-counted overtime (Eagle Destiny Aug-2026). 'ph'/'ot' also only
      // match as whole tokens so e.g. "phone" can't become public holiday.
      const token = (t: string) => new RegExp(`(^|[^a-z])${t}([^a-z]|$)`).test(l);
      if (l.includes('total') || l.includes('sum') || l.includes('gross')) {
        // leave unmapped — the user can still map it deliberately
      } else if (l.includes('night')) guess[i] = 'NIGHT';
      else if (l.includes('holiday') || token('ph')) guess[i] = 'PUBLIC_HOLIDAY';
      else if (l.includes('overtime') || token('ot')) guess[i] = 'OVERTIME';
      else guess[i] = '';
    });
    // One column per hour type: if two headers guessed the same code, keep the
    // first and leave the rest for the user to map (prevents silent overwrite).
    {
      const seen = new Set<string>();
      Object.keys(guess).map(Number).sort((a, b) => a - b).forEach((i) => {
        const c = guess[i];
        if (c && seen.has(c)) delete guess[i];
        else if (c) seen.add(c);
      });
    }

    setColMap(guess);
  };

  const onFile = async (file: File) => applyParsed(parseCsv(await file.text()));

  const onPull = async () => {
    if (!sourceUrl.trim()) return;
    setLoadingSrc(true); setSrcErr(null);
    try {
      const isSheet = sourceUrl.includes('docs.google.com/spreadsheets');
      const res = await fetchHoursSource({ connector: isSheet ? 'google_sheets' : 'url', url: sourceUrl.trim() });
      applyParsed({ headers: res.headers || [], rows: res.rows || [] });
    } catch (e: any) {
      setSrcErr(getApiErrorMessage(e, 'Could not fetch the sheet. Make sure it’s shared “anyone with the link”.'));
    } finally { setLoadingSrc(false); }
  };

  const onScan = async (file: File) => {
    setLoadingSrc(true); setSrcErr(null);
    try {
      const b64 = await new Promise<string>((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(String(fr.result));
        fr.onerror = reject;
        fr.readAsDataURL(file);
      });
      const media = file.type || 'image/jpeg';
      const res = await extractTimesheet({ image_base64: b64, media_type: media });
      if (!res.rows?.length) { setSrcErr('No rows were read from the image. Try a clearer photo.'); return; }
      applyParsed({ headers: res.headers || [], rows: res.rows || [] });
    } catch (e: any) {
      setSrcErr(getApiErrorMessage(e, 'Could not read the timesheet.'));
    } finally { setLoadingSrc(false); }
  };

  const buildRows = (): { rows: ImportHoursRow[]; matched: number; unmatched: string[] } => {
    if (!csv) return { rows: [], matched: 0, unmatched: [] };
    const rows: ImportHoursRow[] = [];
    const unmatched: string[] = [];
    let matched = 0;
    csv.rows.forEach((r, i) => {
      const name = (r[nameCol] || '').trim();
      if (!name || name.toLowerCase() === 'total') return;
      const empId = assign[i];
      const emp = empId ? empById[empId] : undefined;
      const hours: Record<string, string> = {};
      Object.entries(colMap).forEach(([idx, code]) => {
        if (!code) return;
        // Two columns mapped to one code add together (never silently overwrite).
        const v = Number(r[Number(idx)] || '0') || 0;
        hours[code] = String((Number(hours[code] || '0') || 0) + v);
      });
      if (!emp) { unmatched.push(name); return; }
      matched++;
      rows.push({ employee_id: emp.employee_id, name, hours });
    });
    return { rows, matched, unmatched };
  };

  const { rows, matched, unmatched } = csv ? buildRows() : { rows: [], matched: 0, unmatched: [] };
  // Two file rows assigned to the same person would stage twice — surface it.
  const duplicateAssignees = useMemo(() => {
    const seen = new Set<string>();
    const dups = new Set<string>();
    Object.values(assign).forEach((id) => {
      if (!id) return;
      if (seen.has(id)) dups.add(id);
      seen.add(id);
    });
    return dups;
  }, [assign]);

  const run = async (dryRun: boolean) => {
    setBusy(true); setMsg(null);
    try {
      const res = await importHours({ dry_run: dryRun, period: period + '-01', rows });
      setResult(res);
      if (!dryRun) setMsg(`Imported ${res.summary.staged} employees' hours for ${res.period}. Validate the run to price them.`);
    } catch (e: any) {
      setMsg(e?.response?.data?.message || 'Import failed.');
    } finally { setBusy(false); }
  };

  const codes = policy?.hour_types.map((t) => t.code).filter(Boolean) ?? [];

  return (
    <Card>
      <CardContent className="p-5 space-y-4">
        <div>
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2"><Upload className="h-4 w-4 text-primary-500" /> Import worked hours</h3>
          <p className="text-sm text-muted-foreground">Get a period’s hours in three ways — upload a spreadsheet, pull a connected Google Sheet/CSV, or scan a photo/PDF of a signed timesheet. We match employees by name and stage the hours; they’re priced by the policy above when you validate the run.</p>
        </div>

        {policy && effectiveHoursSources(policy).length > 0 && !effectiveHoursSources(policy).includes('file') && (
          <div className="rounded-md border border-warning bg-warning-soft px-3 py-2 text-sm text-warning-strong">
            This entity’s hours sources don’t include <strong>File import</strong> — file-imported hours won’t be
            priced on the run. Tick “File import” under “Hours come from” above to use an import.
          </div>
        )}

        <div className="flex flex-wrap items-end gap-3">
          <label className="space-y-1"><span className={labelCls}>Period</span>
            <input type="month" className={fieldCls + ' py-1 max-w-[170px]'} value={period} onChange={(e) => setPeriod(e.target.value)} /></label>
        </div>

        {/* Source: upload / connected sheet / scan — all land as {headers, rows}. */}
        <div className="flex flex-wrap gap-2">
          {([
            { id: 'file', label: 'Upload file', icon: Upload },
            { id: 'pull', label: 'Connected sheet', icon: Link2 },
            { id: 'scan', label: 'Scan photo/PDF', icon: ScanLine },
          ] as const).map((m) => (
            <button key={m.id} type="button" onClick={() => { setMode(m.id); setSrcErr(null); }}
              className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm ${mode === m.id ? 'bg-orange-500 text-white' : 'bg-muted text-muted-foreground'}`}>
              <m.icon className="h-4 w-4" /> {m.label}
            </button>
          ))}
        </div>

        {mode === 'file' && (
          <input type="file" accept=".csv,text/csv" className="block text-sm" onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])} />
        )}
        {mode === 'pull' && (
          <div className="flex flex-wrap items-center gap-2">
            <input className={fieldCls + ' py-1 max-w-[420px]'} placeholder="Google Sheet share link or CSV URL" value={sourceUrl} onChange={(e) => setSourceUrl(e.target.value)} />
            <Button variant="outline" size="sm" onClick={onPull} disabled={loadingSrc || !sourceUrl.trim()}>
              {loadingSrc ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Link2 className="mr-2 h-4 w-4" />} Fetch
            </Button>
            <span className="text-[11px] text-muted-foreground">Sheet must be shared “anyone with the link”.</span>
          </div>
        )}
        {mode === 'scan' && (
          <div className="space-y-1">
            <input type="file" accept="image/*,application/pdf" className="block text-sm" disabled={loadingSrc} onChange={(e) => e.target.files?.[0] && onScan(e.target.files[0])} />
            <span className="text-[11px] text-muted-foreground">{loadingSrc ? 'Dana is reading the timesheet…' : 'A photo or PDF of a signed timesheet. Dana extracts the hours; review before importing.'}</span>
          </div>
        )}
        {srcErr && <p className="text-sm text-danger">{srcErr}</p>}

        {csv && (
          <>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="space-y-1"><span className={labelCls}>Employee-name column</span>
                <select className={fieldCls + ' py-1'} value={nameCol} onChange={(e) => setNameCol(Number(e.target.value))}>
                  {csv.headers.map((h, i) => <option key={i} value={i}>{h || `Column ${i + 1}`}</option>)}
                </select></label>
            </div>
            <div className="space-y-1">
              <span className={labelCls}>Map columns → hour types</span>
              <div className="overflow-x-auto rounded-md border border-border">
                <table className="w-full text-sm">
                  <tbody>
                    {csv.headers.map((h, i) => i === nameCol ? null : (
                      <tr key={i} className="border-t border-border first:border-t-0">
                        <td className="p-2 text-muted-foreground">{h || `Column ${i + 1}`}</td>
                        <td className="p-2">
                          <select className={fieldCls + ' py-1 max-w-[220px]'} value={colMap[i] ?? ''} onChange={(e) => setColMap({ ...colMap, [i]: e.target.value })}>
                            <option value="">— ignore —</option>
                            {codes.map((c) => <option key={c} value={c}>{c}</option>)}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Who-gets-what review: every file row, its resolved person (editable),
                and the hours that will stage — the Migration-Center pattern. */}
            <div className="space-y-1">
              <span className={labelCls}>Review — whose hours go to who</span>
              <div className="overflow-x-auto rounded-md border border-border">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-muted-foreground bg-muted/50">
                      <th className="p-2 font-medium">Name in file</th>
                      <th className="p-2 font-medium">Employee in KaziPeople</th>
                      {Object.entries(colMap).filter(([, c]) => c).map(([idx, code]) => (
                        <th key={idx} className="p-2 font-medium text-right">{code}</th>
                      ))}
                      <th className="p-2 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {csv.rows.map((r, i) => {
                      const name = (r[nameCol] || '').trim();
                      if (!name || name.toLowerCase() === 'total') return null;
                      const sug = suggestions[i];
                      const chosen = assign[i] ?? '';
                      const isDup = chosen && duplicateAssignees.has(chosen);
                      return (
                        <tr key={i} className="border-t border-border">
                          <td className="p-2 text-foreground">{name}</td>
                          <td className="p-2">
                            <select
                              className={fieldCls + ' py-1 max-w-[240px]' + (!chosen ? ' border-warning' : '')}
                              value={chosen}
                              onChange={(e) => setAssign({ ...assign, [i]: e.target.value })}
                            >
                              <option value="">— skip this row —</option>
                              {sortedEmployees.map((emp) => (
                                <option key={emp.employee_id} value={emp.employee_id}>{emp.full_name}</option>
                              ))}
                            </select>
                          </td>
                          {Object.entries(colMap).filter(([, c]) => c).map(([idx]) => (
                            <td key={idx} className="p-2 text-right tabular-nums text-foreground">{r[Number(idx)] || '0'}</td>
                          ))}
                          <td className="p-2">
                            {isDup ? (
                              <span className="rounded-full bg-danger/15 text-danger px-2 py-0.5 text-xs">Duplicate person</span>
                            ) : !chosen ? (
                              <span className="rounded-full bg-warning-soft text-warning-strong px-2 py-0.5 text-xs">Skipped — pick a person</span>
                            ) : sug?.level === 'exact' ? (
                              <span className="rounded-full bg-success/15 text-success px-2 py-0.5 text-xs">Matched</span>
                            ) : sug?.level === 'strong' && sug.emp?.employee_id === chosen ? (
                              <span className="rounded-full bg-success/15 text-success px-2 py-0.5 text-xs">Matched (name variant)</span>
                            ) : (
                              <span className="rounded-full bg-primary-500/15 text-primary-500 px-2 py-0.5 text-xs">Assigned by you</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="text-sm text-muted-foreground">
                {matched} of {matched + unmatched.length} rows will import.
                {unmatched.length > 0 && (
                  <span className="text-warning"> {unmatched.length} skipped — assign each to a person above, or leave skipped on purpose.</span>
                )}
                {duplicateAssignees.size > 0 && (
                  <span className="text-danger"> Two rows point at the same person — their hours would both stage. Fix before importing.</span>
                )}
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={() => run(true)} disabled={busy || rows.length === 0}>
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Preview (dry run)
              </Button>
              <Button onClick={() => run(false)} disabled={busy || rows.length === 0 || duplicateAssignees.size > 0}>
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />} Import {rows.length || ''} employees
              </Button>
              {msg && <span className="text-sm text-success">{msg}</span>}
            </div>

            {result && (
              <div className="rounded-md bg-muted/40 p-3 text-sm">
                <div className="font-medium text-foreground">{result.dry_run ? 'Dry run' : 'Imported'} · {result.period}</div>
                <div className="text-muted-foreground">Staged {result.summary.staged}, skipped {result.summary.skipped} of {result.summary.total} rows.</div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default OvertimeHoursTab;
